import './styles';

import {useCallback, useEffect} from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {Box} from '@mui/material';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {GROUPS_ROUTES} from '../groups/models/constants';

import {ChatBot} from './components/Chatbot';
import {SPLIT_BILLS_QUERY_PARAMS} from './constants';
import {SplitBillsGroupViewModelProvider} from './SplitBillsGroupViewModelContext';
import {SplitBillsViewModelProvider} from './SplitBillsViewModelContext';

function useKeyboardInset() {
  useEffect(() => {
    const vv = (window as any).visualViewport;
    if (!vv) return;

    const onResize = () => {
      const covered = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      document.documentElement.style.setProperty('--kb', `${covered}px`);
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();

    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
      document.documentElement.style.removeProperty('--kb');
    };
  }, []);
}

function useInputHeightVar(selector = '.chatbot-input-box') {
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el || !('ResizeObserver' in window)) return;

    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--ib', `${h}px`);
    };

    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    setVar();

    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--ib');
    };
  }, [selector]);
}

const SplitBillsContent = () => {
  useKeyboardInset();
  useInputHeightVar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const groupId = searchParams.get(SPLIT_BILLS_QUERY_PARAMS.GROUP_ID);

  const canReturnToGroupDashboard = Boolean(groupId);

  const handleBack = useCallback(() => {
    navigate(`${GROUPS_ROUTES.DASHBOARD}?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  return (
    <Box className="chatbot-container">
      <section className="chatbot-wrapper">
        {canReturnToGroupDashboard && (
          <ArrowBackIosNewIcon
            onClick={handleBack}
            sx={{position: 'absolute', top: 16, left: 16, cursor: 'pointer'}}
          />
        )}
        <ChatBot />
      </section>
    </Box>
  );
};

const SplitBillsPage = () => (
  <SplitBillsViewModelProvider>
    <SplitBillsGroupViewModelProvider>
      <SplitBillsContent />
    </SplitBillsGroupViewModelProvider>
  </SplitBillsViewModelProvider>
);

export default SplitBillsPage;
